/**
 * useBootstrapSession hook module.
 * Provides a one-shot hook to initialize the user session, log the user/rep into context, and trigger initial data fetches.
 */

// External packages
import { useEffect } from "react";

// Internal shared modules
import { useAuthContext } from "../../../context/AuthContext";
import { useAccountContext } from "../../../context/AccountContext";

// Relative imports - API
import { checkSession } from "../../../api/auth/checkSessionApi";

// Relative imports - Hooks
import useInitialUserData from "./useInitialUserData";

/**
 * Custom React hook that boots the session via /check-session, logs the user/rep into context,
 * and triggers initial "light" data fetch. Should be called once at app startup.
 *
 * @param {Object} [options] - Hook options.
 * @param {boolean} [options.skip=false] - Skip the bootstrap entirely (e.g. rep-landing, which authorizes before checking the session).
 *
 * Side effects: Updates user context and triggers initial data fetches.
 */
export default function useBootstrapSession({ skip = false } = {}) {
  const { setIsLoggedIn, setIsSessionLoading, loginUser, loginRep } =
    useAuthContext();
  const { storeAccountData } = useAccountContext();

  // Pulls account / subscription / billing once logged-in
  useInitialUserData();

  useEffect(() => {
    if (skip) {
      return;
    }

    (async () => {
      setIsSessionLoading(true);
      const json = await checkSession();

      if (json?.status === 1 && json.data) {
        // User is logged in
        if (json.data.user_type === "representative") {
          loginRep(json.data);
          // Store cached account data immediately for rep-with-user-data prefill.
          // This avoids a race with fullSaleReset clearing the backend session
          // before getAccountLight can fetch it.
          if (json.data.account_light) {
            const { subscriptions, ...accountFields } = json.data.account_light;
            storeAccountData(accountFields);
          }
        } else if (json.data.user_type === "user") {
          loginUser(json.data);
        } else {
          setIsLoggedIn(false);
        }
      } else {
        // Not logged in
        setIsLoggedIn(false);
      }

      setIsSessionLoading(false);
    })();
  }, [skip]); // eslint-disable-line react-hooks/exhaustive-deps
}
